'use client';
import { useEffect } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import { hasConsent } from './consent';

export function useGoogleAnalytics() {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    const measurementId = process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID;
    if (!measurementId) return;

    // Check if user has given analytics consent
    const hasAnalyticsConsent = hasConsent('analytics');
    if (!hasAnalyticsConsent) return;

    if (!window.dataLayer) {
      const script = document.createElement('script');
      script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
      script.async = true;
      document.head.appendChild(script);

      window.dataLayer = [];
      window.gtag = function(...args: any[]) { window.dataLayer.push(args); };
      window.gtag('js', new Date());
      window.gtag('config', measurementId);
    }

    const url = pathname + (searchParams.toString() ? `?${searchParams}` : '');
    window.gtag('event', 'page_view', { page_path: url });
  }, [pathname, searchParams]);
}

export function trackEvent(eventName: string, eventParams: Record<string, any> = {}) {
  if (window.gtag && hasConsent('analytics')) {
    window.gtag('event', eventName, eventParams);
  }
}

// Sales funnel specific tracking events
export const trackDiagnosisStart = () => {
  trackEvent('diagnosis_started', {
    event_category: 'sales_funnel',
    event_label: 'Online Diagnosis Started',
    funnel_step: 1
  });
};

export const trackDiagnosisStep = (step: number, stepName: string) => {
  trackEvent('diagnosis_step_completed', {
    event_category: 'sales_funnel',
    event_label: stepName,
    funnel_step: step + 1,
    step_number: step
  });
};

export const trackDiagnosisCompleted = (formData: any) => {
  trackEvent('diagnosis_completed', {
    event_category: 'sales_funnel',
    event_label: 'Online Diagnosis Completed',
    funnel_step: 2,
    life_stage: formData.lifeStage,
    goals_count: formData.financialGoals?.length || 0,
    has_existing_planning: formData.existingPlanning !== 'none'
  });
};

export const trackBookingIntent = (source: string) => {
  trackEvent('booking_intent', {
    event_category: 'sales_funnel',
    event_label: `Booking CTA Clicked - ${source}`,
    funnel_step: 3,
    cta_source: source
  });
};

export const trackConversion = (conversionType: string, value?: number) => {
  trackEvent('conversion', {
    event_category: 'sales_funnel',
    event_label: conversionType,
    funnel_step: 4,
    conversion_type: conversionType,
    value: value || 0
  });
};

export const trackCTAClick = (ctaName: string, location: string) => {
  trackEvent('cta_click', {
    event_category: 'engagement',
    event_label: ctaName,
    cta_location: location
  });
};

// Type declarations for gtag
declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
  }
}